import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { servicesData, localServiceAreas } from '../../data/servicesData';
import SEO from '../../components/SEO';
import './styles/ServicePage.css';

const renderFormattedText = (text) => {
  // Split the text into paragraphs and lists
  const lines = text.split('\n');
  const formattedContent = [];
  let currentList = null;

  lines.forEach((line, index) => {
    // Check for headings
    if (line.startsWith('## ')) {
      formattedContent.push(
        <h3 key={`heading-${index}`} className="overview-heading">
          {line.replace('## ', '')}
        </h3>
      );
    } 
    // Check for list items
    else if (line.startsWith('- ')) {
      if (!currentList) {
        currentList = [];
      }
      currentList.push(
        <li key={`list-item-${index}`}>
          {line.replace('- ', '')}
        </li>
      );
    } 
    // Check for empty line to close list
    else if (line.trim() === '' && currentList) {
      formattedContent.push(
        <ul key={`list-${formattedContent.length}`} className="overview-list">
          {currentList}
        </ul>
      );
      currentList = null;
    } 
    // Regular paragraphs
    else if (line.trim() !== '') {
      // Close any open list before adding paragraph
      if (currentList) {
        formattedContent.push(
          <ul key={`list-${formattedContent.length}`} className="overview-list">
            {currentList}
          </ul>
        );
        currentList = null;
      }
      formattedContent.push(
        <p key={`para-${index}`} className="overview-paragraph">
          {line}
        </p>
      );
    }
  });

  // Close any remaining list
  if (currentList) {
    formattedContent.push(
      <ul key={`list-final`} className="overview-list">
        {currentList}
      </ul>
    );
  }

  return formattedContent;
};

const ServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = servicesData.find(s => s.slug === slug);

  useEffect(() => {
    if (!service) {
      navigate('/services');
    }
    window.scrollTo(0, 0);
  }, [service, navigate]);

  if (!service) return null;

  // Generate service-specific SEO metadata
  const seoMetadata = {
    title: `${service.title} Services in Reno, Nevada | Grizzly Tree Company`,
    description: `Professional ${service.title.toLowerCase()} services in Northern Nevada. ${service.subtitle}. Expert arborist care tailored to your needs.`,
    keywords: `${service.title.toLowerCase()}, tree service, Reno Nevada, Northern Nevada tree care, arborist services`,
    canonicalPath: `/services/${slug}`,
    ogImage: service.heroImage,
    jsonLd: {
      "@type": "Service",
      "name": `${service.title} Services`,
      "description": service.subtitle
    }
  };

  // Optional: Service-specific schema extension
  const serviceSchema = {
    "serviceType": service.title,
    "areaServed": service.metadata?.serviceAreas || localServiceAreas
  };

  return (
    <>
      <SEO 
        page="services" 
        additionalMetadata={seoMetadata}
        serviceSchema={serviceSchema}
      />

      <div className="service-page">
        <div 
          className="service-hero" 
          style={{ backgroundImage: `url(${service.heroImage})` }}
        >
          <div className="hero-overlay"></div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {service.title}
            </motion.h1>
            <motion.p
              className="hero-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {service.subtitle}
            </motion.p>
          </div>
        </div>

        <div className="service-content">
          <div className="content-wrapper">
            <section className="overview-section">
              <h2 className="section-title">Overview</h2>
              {renderFormattedText(service.fullDescription)}
            </section>

            <section className="overview-cta-section">
              <Link to="/contact" className="overview-contact-link">
                <motion.button
                  className="overview-contact-button"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  whileHover={{ 
                    scale: 1.05,
                    transition: { duration: 0.2 }
                  }}
                >
                  Learn More About Our Services
                </motion.button>
              </Link>
            </section>

            <section className="features-section">
              <h2 className="section-title">Key Features</h2>
              <div className="features-grid">
                {service.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="feature-card"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="feature-icon">
                      <i className={feature.icon}></i>
                    </div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </motion.div>
                ))}
              </div>
            </section>

            <section className="benefits-section">
              <h2 className="section-title">Benefits</h2>
              <div className="benefits-container">
                {service.benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    className="benefit-item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <span className="benefit-number">{index + 1}</span>
                    <h3>{benefit.title}</h3>
                    <p>{benefit.description}</p>
                  </motion.div>
                ))}
              </div>
            </section>

            {service.tips && (
              <section className="tips-section">
                <h2 className="section-title">Expert Tips</h2>
                <div className="tips-container">
                  {service.tips.map((tip, index) => (
                    <motion.div
                      key={index}
                      className="tip-item"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <h3>{tip.title}</h3>
                      <p>{tip.description}</p>
                      <Link to={tip.link} className="tip-contact-link">
                        Learn More
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            <section className="cta-section">
              <motion.div
                className="cta-content"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <h2>Ready to Get Started?</h2>
                <p>{service.ctaText}</p>
                <Link to="/contact" className="overview-contact-link">
                  <motion.button
                    className="overview-contact-button"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    whileHover={{ 
                      scale: 1.05,
                      transition: { duration: 0.2 }
                    }}
                  >
                    Contact Us Now
                  </motion.button>
                </Link>
              </motion.div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServicePage; 